import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KpiCard, secondaryTextClassName } from '@monecolier/ui';
import type { AggregatedParentFinancialOverview } from '@monecolier/api-client';

// PAY-20 : "Montant brut / Réduction" AGRÉGÉ — Étape 0 a confirmé que
// `grossAmountDue`/`amountDue` sont DÉJÀ présents par échéance (PAY-10, via
// `child.invoices[]`), aucune extension backend nécessaire pour cette ligne
// (pas d'équivalent agrégé au niveau serveur, calcul CLIENT sur les données
// déjà chargées). Centimes ENTIERS — même précaution que
// `StudentFinancialOverview.tsx#subtractDecimalStrings` (PAY-01), jamais une
// soustraction flottante directe sur des chaînes "X.YY".
function sumInvoiceCents(overview: AggregatedParentFinancialOverview, field: 'grossAmountDue' | 'amountDue'): number {
  let cents = 0;
  for (const child of overview.children) {
    for (const invoice of child.invoices) {
      cents += Math.round(Number(invoice[field]) * 100);
    }
  }
  return cents;
}

/**
 * Refonte de navigation web-parent — extrait de `Home.tsx` (PAR-05) pour être
 * réutilisé par la page `/payments` (groupe "Paiements" de la Sidebar,
 * UX-17) SANS dupliquer le calcul ni l'affichage : sélecteur
 * d'enfant PROÉMINENT + totaux agrégés, tous tenants (écoles) confondus.
 * `computeAggregatedParentFinancialOverview()` (côté serveur, PAR-05) réutilise
 * `computeParentStudentFinancialOverview()` (PAY-01) UNE FOIS PAR ENFANT et
 * agrège — chaque entrée de `children` est donc la MÊME forme que la page
 * "Situation financière" individuelle (`StudentFinancialOverview.tsx`), vers
 * laquelle chaque pastille du sélecteur mène directement.
 *
 * Purement présentationnel — AUCUN appel réseau ici (même discipline que
 * `TodayCollectionsPanel`, web-admin) : chaque page appelante garde son propre
 * `useState`/`useEffect`/`getAggregatedParentFinancialOverview()`. Ne rend
 * rien s'il n'existe aucun enfant avec une situation financière réelle
 * (`Student` promu, ENR-01) — un parent qui n'a encore QUE des enfants
 * déclarés (PAR-02, jamais inscrits) n'a rien à agréger, même comportement
 * qu'avant l'extraction.
 */
export function AggregatedFinancialPanel({ overview }: { overview: AggregatedParentFinancialOverview | null }) {
  const { t } = useTranslation();
  if (!overview || overview.children.length === 0) return null;

  const grossCents = sumInvoiceCents(overview, 'grossAmountDue');
  const netCents = sumInvoiceCents(overview, 'amountDue');
  const discountCents = grossCents - netCents;
  const currency = overview.children[0]?.student.currency;

  return (
    <div data-testid="multi-child-overview" className="rounded-md border border-gray-200 p-3 flex flex-col gap-3">
      <div>
        <p className={`${secondaryTextClassName} font-medium`}>{t('aggregatedFinancialPanel.myChildren')}</p>
        <div data-testid="child-selector" className="flex flex-wrap gap-2 mt-1">
          {overview.children.map((child) => (
            <Link
              key={child.student.id}
              to={`/students/${child.student.id}/financial-overview`}
              data-testid={`child-selector-${child.student.id}`}
              className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
            >
              {child.student.firstName} {child.student.lastName} — {child.student.schoolName}
            </Link>
          ))}
        </div>
      </div>
      {/* MOB-10 : `grid-cols-3` seul (sans breakpoint) chevauchait visuellement
          les montants sur un viewport WebView Android étroit (~360-400px,
          confirmé sur appareil réel) — un montant de 13+ caractères n'a pas la
          place de tenir dans ~1/3 de la largeur disponible. Empilé en 1
          colonne sous `sm:` (640px) — en paysage, un téléphone dépasse déjà
          cette largeur, donc les 3 colonnes réapparaissent naturellement. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="aggregated-financial-totals">
        {/* Total dû/payé : purement informatifs, jamais de sens "bien/mal" à
            leur attribuer seuls — tone neutre. Solde restant : SEUL total qui
            porte un vrai signal ("soldé" ou non) — succès si 0 (rien à payer),
            à traiter sinon, calculé ici (rendu pur, AUCUN changement de
            calcul métier — `totalOutstanding` reste calculé tel quel côté
            serveur, PAR-05/PAY-01). */}
        <KpiCard
          label={t('aggregatedFinancialPanel.totalDue')}
          value={`${overview.totalDue} ${overview.children[0]?.student.currency}`}
          data-testid="aggregated-total-due"
        />
        <KpiCard
          label={t('aggregatedFinancialPanel.totalPaid')}
          value={`${overview.totalPaid} ${overview.children[0]?.student.currency}`}
          data-testid="aggregated-total-paid"
        />
        <KpiCard
          label={t('aggregatedFinancialPanel.totalOutstanding')}
          value={`${overview.totalOutstanding} ${overview.children[0]?.student.currency}`}
          tone={Number(overview.totalOutstanding) <= 0 ? 'success' : 'attention'}
          data-testid="aggregated-total-outstanding"
        />
      </div>
      {/* PAY-20 — visible UNIQUEMENT si au moins une réduction (PAY-10) est
          active sur au moins une échéance, jamais une ligne fabriquée quand
          brut == net (même règle déjà établie par
          StudentFinancialOverview.tsx#invoice-discount-detail). */}
      {discountCents > 0 ? (
        <p data-testid="aggregated-gross-discount" className={secondaryTextClassName}>
          {t('aggregatedFinancialPanel.grossDiscount', {
            gross: (grossCents / 100).toFixed(2),
            discount: (discountCents / 100).toFixed(2),
            currency,
          })}
        </p>
      ) : null}
      {/* PAY-24 — purement informatif, JAMAIS d'action possible ici (voir
          PAY-22, hors périmètre) : visible UNIQUEMENT si le total sur TOUS
          les enfants est > 0, absent sinon (jamais "0 XOF" affiché sans
          raison). */}
      {Number(overview.adjustmentsTotal) > 0 ? (
        <p data-testid="aggregated-adjustments-total" className={secondaryTextClassName}>
          {t('aggregatedFinancialPanel.adjustmentsTotal', {
            amount: overview.adjustmentsTotal,
            currency: overview.children[0]?.student.currency,
          })}
        </p>
      ) : null}
      {/* Cas limite (performance) — voir lib/parentFinancialOverview.ts côté
          serveur : jamais une troncature silencieuse, le parent est informé
          explicitement si sa fratrie dépasse le plafond agrégé. */}
      {overview.truncated ? (
        <p data-testid="aggregated-overview-truncated" className={secondaryTextClassName}>
          {t('aggregatedFinancialPanel.truncated')}
        </p>
      ) : null}
    </div>
  );
}
